// client/src/components/pages/LexiStar/RecomputeJobs/RecomputeJobDetail.jsx
//
// One recompute job and its before/after diff (LS-09 §5.19; T4-S12):
//   state + progress (polled while the engine works; `lexistar:recompute`
//   refetches on a state change), the headline deltas, the signed-off trips
//   the recompute touches — listed apart, because publishing turns them into
//   amendment requests for a second user instead of changing them — and the
//   changed records with old and new values side by side (table or map).
// Publish is the second user's sign-off (SR-21): disabled for the requester
// and without lexistar.recompute.signoff, always saying why. Discard leaves
// history untouched. A published job stays viewable with its diff.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowLeft, FaCheck, FaTrashAlt, FaSyncAlt } from 'react-icons/fa';
import SegmentedControl from '../../../common/SegmentedControl';
import { useAuth } from '../../../../context/AuthContext';
import {
  getRecomputeJob,
  getRecomputeDiff,
  signOffRecomputeJob,
  discardRecomputeJob,
  pickObject,
} from '../../../../services/lexistarAssurance';
import { ErrorBanner, NoticeBanner, LoadingState, cardClasses } from '../StatusBoard/atoms';
import { fmtDateTime, fmtNum } from '../StatusBoard/format';
import {
  KpiCard,
  Modal,
  ProgressBar,
  InlineError,
  buttonClasses,
} from '../ReviewQueue/assuranceAtoms';
import { errorMessage } from '../ReviewQueue/assuranceShared';
import {
  jobStateMeta,
  isActiveJob,
  progressRatio,
  publishGate,
  triggerLabel,
  scopeSummary,
  headlineCards,
  splitDiffRows,
  TABLE_LABELS,
  DISCARDABLE,
} from './recomputeLogic';
import DiffTable from './DiffTable';
import DiffMap from './DiffMap';

const POLL_MS = 5000;
const CHANGE_FILTERS = [
  { value: '', label: 'All' },
  { value: 'changed', label: 'Changed' },
  { value: 'added', label: 'Added' },
  { value: 'removed', label: 'Removed' },
];

export function JobStateChip({ state }) {
  const meta = jobStateMeta(state);
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${state === 'awaiting_signoff' || state === 'computed' ? 'bg-slate-900 text-white' : state === 'failed' ? 'bg-surface text-red-700 ring-1 ring-inset ring-red-400' : 'bg-slate-100 text-slate-700'}`}
      data-state={state}
    >
      <span aria-hidden="true">{meta.glyph}</span>
      {meta.label}
    </span>
  );
}

export default function RecomputeJobDetail({ id, onChanged, recomputeSignal }) {
  const { user, hasPermission } = useAuth();
  const canRun = hasPermission('lexistar.recompute.run');
  const canSignOff = hasPermission('lexistar.recompute.signoff');
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [diff, setDiff] = useState({ rows: [], signedOff: [], pagination: null, tables: [] });
  const [diffLoading, setDiffLoading] = useState(false);
  const [table, setTable] = useState('');
  const [change, setChange] = useState('');
  const [page, setPage] = useState(1);
  const [view, setView] = useState('table');
  const [confirm, setConfirm] = useState(null); // 'publish' | 'discard'
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState(null);
  const pollTimer = useRef(null);

  const loadJob = useCallback(
    async ({ quiet = false } = {}) => {
      try {
        if (!quiet) setLoading(true);
        const res = await getRecomputeJob(id);
        setJob(pickObject(res, 'job', 'recompute_job'));
        setError(null);
      } catch (err) {
        if (!quiet)
          setError(
            err.response?.status === 404
              ? 'Recompute job not found, or not visible to your organisation.'
              : errorMessage(err, 'Failed to load the recompute job.')
          );
      } finally {
        if (!quiet) setLoading(false);
      }
    },
    [id]
  );

  useEffect(() => {
    loadJob();
  }, [loadJob]);

  // Progress is polled (contract §5.2: no progress events), state changes arrive live.
  useEffect(() => {
    clearTimeout(pollTimer.current);
    if (job && isActiveJob(job)) {
      pollTimer.current = setTimeout(() => loadJob({ quiet: true }), POLL_MS);
    }
    return () => clearTimeout(pollTimer.current);
  }, [job, loadJob]);

  useEffect(() => {
    if (recomputeSignal) loadJob({ quiet: true });
  }, [recomputeSignal, loadJob]);

  const hasDiff =
    job && ['computed', 'awaiting_signoff', 'publishing', 'published'].includes(job.state);

  const loadDiff = useCallback(async () => {
    if (!hasDiff) return;
    setDiffLoading(true);
    try {
      const res = await getRecomputeDiff(id, { table, change, page });
      setDiff(splitDiffRows(res));
    } catch (err) {
      setError(errorMessage(err, 'Failed to load the diff.'));
    } finally {
      setDiffLoading(false);
    }
  }, [id, table, change, page, hasDiff]);

  useEffect(() => {
    loadDiff();
  }, [loadDiff]);

  const gate = publishGate({ job, user, canSignOff });
  const canDiscard = (canRun || canSignOff) && job && DISCARDABLE.includes(job.state);

  const act = async () => {
    setBusy(true);
    setActionError(null);
    try {
      if (confirm === 'publish') {
        await signOffRecomputeJob(id);
        setNotice(
          'Signed off. The engine is publishing the diff; signed-off trips become amendment requests.'
        );
      } else {
        await discardRecomputeJob(id);
        setNotice('Discarded. History is untouched.');
      }
      setConfirm(null);
      await loadJob({ quiet: true });
      onChanged?.();
    } catch (err) {
      setActionError(
        errorMessage(
          err,
          confirm === 'publish' ? 'The job was not published.' : 'The job was not discarded.'
        )
      );
    } finally {
      setBusy(false);
    }
  };

  if (loading && !job) return <LoadingState label="Loading the recompute job..." />;
  if (!job)
    return (
      <>
        <ErrorBanner message={error} />
        <Link to="/lexistar/recompute-jobs" className={buttonClasses.secondary}>
          <FaArrowLeft size={11} /> All recompute jobs
        </Link>
      </>
    );

  const cards = headlineCards(job.diff);
  const tables = diff.tables.length
    ? diff.tables
    : [...new Set(diff.rows.map((r) => r.table_name))].map((t) => ({ table_name: t }));
  const estimate = job.estimate || {};
  const progress = job.progress || {};

  return (
    <div className="space-y-5">
      <ErrorBanner message={error} onDismiss={() => setError(null)} />
      <NoticeBanner message={notice} onDismiss={() => setNotice(null)} />

      <div>
        <Link
          to="/lexistar/recompute-jobs"
          className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900"
        >
          <FaArrowLeft size={10} /> All recompute jobs
        </Link>
      </div>

      <section className={`${cardClasses} p-4 sm:p-5`} aria-label="Job">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="min-w-0 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-bold text-slate-900">{triggerLabel(job.trigger)}</h2>
              <JobStateChip state={job.state} />
            </div>
            <p className="text-sm text-slate-600">
              {scopeSummary({ ...(job.scope || {}), ...(job.range ? { range: job.range } : {}) })}
            </p>
            <p className="text-xs text-slate-500">
              Requested by{' '}
              <span className="font-medium text-slate-700">{job.requested_by || '—'}</span> ·{' '}
              {fmtDateTime(job.created_at)}
              {job.config_snapshot_id && (
                <>
                  {' '}
                  · configuration <span className="font-mono">{job.config_snapshot_id}</span>
                </>
              )}
            </p>
            {job.signed_off_by && (
              <p className="text-xs text-slate-500">
                Signed off by{' '}
                <span className="font-medium text-slate-700">{job.signed_off_by}</span> ·{' '}
                {fmtDateTime(job.signed_off_at)}
              </p>
            )}
          </div>
          <div className="flex flex-col items-stretch sm:items-end gap-2 lg:max-w-sm">
            <div className="flex flex-wrap gap-2 sm:justify-end">
              <button
                type="button"
                onClick={() => loadJob()}
                aria-label="Refresh"
                className={buttonClasses.secondary}
              >
                <FaSyncAlt size={11} />
              </button>
              {canDiscard && (
                <button
                  type="button"
                  onClick={() => {
                    setActionError(null);
                    setConfirm('discard');
                  }}
                  className={buttonClasses.danger}
                >
                  <FaTrashAlt size={11} /> Discard
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setActionError(null);
                  setConfirm('publish');
                }}
                disabled={!gate.allowed}
                aria-describedby={gate.reason ? 'publish-gate' : undefined}
                className={buttonClasses.primary}
              >
                <FaCheck size={11} /> Publish
              </button>
            </div>
            {gate.reason && job.state !== 'published' && (
              <p id="publish-gate" className="text-xs text-slate-600 sm:text-right">
                {gate.reason}
              </p>
            )}
          </div>
        </div>

        {isActiveJob(job) && job.state !== 'publishing' && (
          <div className="mt-4 space-y-1">
            <ProgressBar value={progressRatio(job)} label="Recompute progress" />
            <p className="text-xs text-slate-500 tabular-nums">
              {fmtNum(progress.partitions_done ?? 0)} of {fmtNum(progress.partitions_total ?? 0)}{' '}
              trucks · {fmtNum(progress.records_read ?? 0)} records read · estimate{' '}
              {fmtNum(estimate.positions)} positions
            </p>
          </div>
        )}
        {job.state === 'failed' && job.error && (
          <p className="mt-3 text-sm text-red-700">
            Failed: {job.error}. Current data was not touched.
          </p>
        )}
        {job.state === 'published' && (
          <p className="mt-3 text-sm text-slate-700">
            Published {fmtDateTime(job.published_at)}. This diff is kept permanently; superseded
            records are in the history schema.
          </p>
        )}
      </section>

      {cards.length > 0 && (
        <section
          aria-label="Headline deltas"
          className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3"
        >
          {cards.map((c) => (
            <KpiCard
              key={c.key}
              label={c.label}
              value={c.value}
              hint={c.hint}
              tone={c.warn ? 'warn' : 'default'}
            />
          ))}
        </section>
      )}

      {hasDiff && (
        <>
          {diff.signedOff.length > 0 && (
            <section
              className={`${cardClasses} p-4 sm:p-5 border-amber-300`}
              aria-label="Signed-off trips"
            >
              <h3 className="text-sm font-bold text-slate-900">
                Signed-off trips — proposed amendments ({diff.signedOff.length})
              </h3>
              <p className="text-sm text-slate-600 mt-1">
                These trips are locked. Publishing does not change them; each becomes an amendment
                request that a second user approves on the trip.
              </p>
              <DiffTable rows={diff.signedOff} />
            </section>
          )}

          <section className={`${cardClasses} p-4 sm:p-5`} aria-label="Changed records">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-3">
              <h3 className="text-sm font-bold text-slate-900">Changed records</h3>
              <SegmentedControl
                options={[
                  { value: 'table', label: 'Table' },
                  { value: 'map', label: 'Map' },
                ]}
                value={view}
                onChange={setView}
                ariaLabel="Diff view"
              />
            </div>
            <div className="flex flex-wrap gap-2 mb-3" role="group" aria-label="Filter the diff">
              {[{ table_name: '' }, ...tables].map((t) => (
                <button
                  key={t.table_name || 'all'}
                  type="button"
                  aria-pressed={table === t.table_name}
                  onClick={() => {
                    setTable(t.table_name);
                    setPage(1);
                  }}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border ${table === t.table_name ? 'bg-slate-900 text-white border-slate-900' : 'bg-surface text-slate-700 border-slate-300 hover:bg-slate-50'}`}
                >
                  {t.table_name ? TABLE_LABELS[t.table_name] || t.table_name : 'All tables'}
                  {t.count != null && (
                    <span className="ml-1 tabular-nums opacity-80">{t.count}</span>
                  )}
                </button>
              ))}
              <span className="w-px bg-slate-200 mx-1" aria-hidden="true" />
              {CHANGE_FILTERS.map((c) => (
                <button
                  key={c.value || 'any'}
                  type="button"
                  aria-pressed={change === c.value}
                  onClick={() => {
                    setChange(c.value);
                    setPage(1);
                  }}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border ${change === c.value ? 'bg-slate-900 text-white border-slate-900' : 'bg-surface text-slate-700 border-slate-300 hover:bg-slate-50'}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
            {diffLoading ? (
              <div className="h-40 rounded-lg bg-slate-100 animate-pulse" aria-hidden="true" />
            ) : view === 'map' ? (
              <DiffMap rows={diff.rows} />
            ) : (
              <DiffTable rows={diff.rows} />
            )}
            {diff.pagination && Number(diff.pagination.totalPages || diff.pagination.pages) > 1 && (
              <div className="flex items-center justify-between mt-3 text-sm">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                  className={buttonClasses.secondary}
                >
                  Previous
                </button>
                <span className="tabular-nums text-slate-600">
                  Page {page} of {diff.pagination.totalPages || diff.pagination.pages}
                </span>
                <button
                  type="button"
                  disabled={page >= Number(diff.pagination.totalPages || diff.pagination.pages)}
                  onClick={() => setPage((p) => p + 1)}
                  className={buttonClasses.secondary}
                >
                  Next
                </button>
              </div>
            )}
          </section>
        </>
      )}

      <Modal
        open={!!confirm}
        title={confirm === 'publish' ? 'Publish this recompute' : 'Discard this recompute'}
        onClose={() => setConfirm(null)}
        footer={
          <>
            <button
              type="button"
              onClick={() => setConfirm(null)}
              className={buttonClasses.secondary}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={act}
              disabled={busy}
              className={confirm === 'publish' ? buttonClasses.primary : buttonClasses.danger}
            >
              {busy ? 'Working…' : confirm === 'publish' ? 'Sign off and publish' : 'Discard'}
            </button>
          </>
        }
      >
        {confirm === 'publish' ? (
          <p className="text-sm text-slate-700">
            You are the second user for this recompute (requested by{' '}
            <span className="font-medium">{job.requested_by}</span>). Publishing replaces the
            current records with the recomputed ones, keeps the old rows in history, re-anchors
            corrections and turns changes to{' '}
            <span className="tabular-nums font-semibold">
              {fmtNum(job.diff?.signed_off_trips_affected ?? diff.signedOff.length)}
            </span>{' '}
            signed-off trip(s) into amendment requests. It is written to the audit ledger under your
            name.
          </p>
        ) : (
          <p className="text-sm text-slate-700">
            The shadow results are dropped and nothing in history changes. The job stays listed as
            discarded.
          </p>
        )}
        <InlineError message={actionError} />
      </Modal>
    </div>
  );
}
